import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MealImageGallery } from "@/components/meal/MealImageGallery";
import { MealBasicInfo } from "@/components/meal/MealBasicInfo";
import { MealChefInfo } from "@/components/meal/MealChefInfo";
import { MealIngredients } from "@/components/meal/MealIngredients";
import { MealReviews } from "@/components/meal/MealReviews";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { calculateDistance, formatDistance } from "@/utils/distance";
import { useEffect } from "react";
import { NutritionalInfo } from "@/components/meal/NutritionalInfo";
import { MessageChef } from "@/components/meal/MessageChef";
import { Json } from "@/integrations/supabase/types";
import { useLocationServices } from "@/hooks/useLocationServices";

interface AvailabilitySlot {
  date: string;
  slots: string[];
}

interface SlotData {
  date?: string;
  slots?: string[];
}

const MealDetails = () => {
  const { mealId } = useParams();
  const navigate = useNavigate();
  
  const { locationState, refreshLocation } = useLocationServices();
  const userLocation = locationState.coords;

  useEffect(() => {
    if (locationState.permission === 'granted' && !locationState.isLoading) {
      refreshLocation();
    }
  }, [locationState.permission, locationState.isLoading, refreshLocation]);

  const { data: meal, isLoading } = useQuery({
    queryKey: ['meal', mealId, userLocation],
    queryFn: async () => {
      if (!mealId) throw new Error('Meal ID is required');

      console.log('Fetching meal data...');

      const { data: mealData, error: mealError } = await supabase
        .from('meals')
        .select(`
          *,
          chef:profiles!meals_chef_id_fkey(
            id,
            first_name,
            last_name,
            image_url,
            chef_rating,
            latitude,
            longitude
          )
        `)
        .eq('id', mealId)
        .single();

      if (mealError) {
        console.error('Error fetching meal:', mealError);
        throw mealError;
      }
      if (!mealData) throw new Error('Meal not found');

      console.log('Meal data:', mealData);

      let distance = "Distance unavailable";
      
      const { data: userData } = await supabase.auth.getUser();
      const currentUserId = userData.user?.id;
      const isOwnMeal = currentUserId && mealData.chef?.id === currentUserId;
      
      if (isOwnMeal) {
        distance = "Your meal";
      } else if (userLocation && mealData.chef?.latitude && mealData.chef?.longitude) {
        console.log('Calculating distance with coordinates:', {
          userLat: userLocation.lat,
          userLng: userLocation.lng,
          chefLat: mealData.chef.latitude,
          chefLng: mealData.chef.longitude
        });
        
        const calculatedDistance = calculateDistance(
          userLocation.lat,
          userLocation.lng,
          Number(mealData.chef.latitude),
          Number(mealData.chef.longitude)
        );
        
        console.log('Calculated distance:', calculatedDistance);
        distance = calculatedDistance !== null ? formatDistance(calculatedDistance) : "Distance unavailable";
      } else {
        console.log('Cannot calculate distance. User location or chef coordinates missing:', {
          userLocation,
          chefLat: mealData.chef?.latitude,
          chefLng: mealData.chef?.longitude
        });
      }

      return {
        ...mealData,
        distance
      };
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="space-y-8">
            <Skeleton className="h-[300px] w-full rounded-lg" />
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!meal) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Meal not found</h1>
          <Button onClick={() => navigate("/")}>Back to Home</Button>
        </div>
      </div>
    );
  }

  console.log('Raw availability slots:', meal.availability_slots);
  
  if (Array.isArray(meal.availability_slots) && meal.availability_slots.length > 0) {
    const firstSlot = meal.availability_slots[0];
    console.log('First slot deep inspection:', JSON.stringify(firstSlot, null, 2));
  }
  
  const parsedAvailabilitySlots = Array.isArray(meal.availability_slots) 
    ? meal.availability_slots.map((rawSlot: Json) => {
      console.log('Processing raw slot:', rawSlot);
      
      if (typeof rawSlot === 'object' && rawSlot !== null && !Array.isArray(rawSlot)) {
        const slot = rawSlot as Record<string, unknown>;
        if (
          typeof slot.startTime === 'string' && 
          typeof slot.endTime === 'string' && 
          slot.dateRange && 
          typeof slot.dateRange === 'object'
        ) {
          const dateRange = slot.dateRange as Record<string, unknown>;
          const startDate = typeof dateRange.from === 'string' ? dateRange.from : '';
          const endDate = typeof dateRange.to === 'string' ? dateRange.to : startDate;
          
          let recurring: { days: string[]; until: string } | undefined;
          if (slot.recurring && typeof slot.recurring === 'object') {
            const recData = slot.recurring as Record<string, unknown>;
            if (Array.isArray(recData.days) && typeof recData.until === 'string') {
              recurring = {
                days: recData.days as string[],
                until: recData.until
              };
            }
          }
          
          if (startDate) {
            return {
              date: startDate,
              endDate: endDate,
              slots: [`${slot.startTime} - ${slot.endTime}`],
              recurring
            };
          }
        }
      }
      return { date: '', slots: [] };
    }).filter(slot => slot.date && slot.slots.length > 0)
    : [];

  console.log('Parsed availability slots:', parsedAvailabilitySlots);

  const transformedMeal = {
    id: meal.id,
    name: meal.name,
    chef: meal.chef ? {
      id: meal.chef.id,
      first_name: meal.chef.first_name,
      last_name: meal.chef.last_name
    } : undefined,
    chef_id: meal.chef?.id || '',
    chefRating: Number(meal.chef?.chef_rating) || 5,
    price: Number(meal.price) || 0,
    description: meal.description || '',
    preparationType: (meal.preparation_type === 'on_demand' ? 'on_demand' : 'scheduled') as 'scheduled' | 'on_demand',
    prepTimeHours: meal.prep_time_hours,
    cutoffTime: meal.cutoff_time,
    pickupWindowStart: meal.pickup_window_start,
    pickupWindowEnd: meal.pickup_window_end,
    availableDays: meal.available_days || [],
    maxDailyOrders: meal.max_daily_orders,
    preparationTime: meal.preparation_time || '30 mins',
    ingredients: meal.ingredients || [],
    allergens: meal.allergens || [],
    dietaryTags: meal.dietary_tags || [],
    image: meal.image || 'https://images.unsplash.com/photo-1574894709920-11b28e7367e3?auto=format&fit=crop&q=80',
    rating: Number(meal.rating) || 0,
    reviews: Number(meal.reviews_count) || 0,
    scheduledDate: meal.scheduled_date,
    availablePortions: meal.available_portions !== null ? Number(meal.available_portions) : null,
    distance: meal.distance,
    deliveryFee: Number(meal.delivery_fee) || 2.99,
    chefImage: meal.chef?.image_url || '/placeholder.svg',
    calories: meal.calories,
    carbs: meal.carbs,
    protein: meal.protein,
    fat: meal.fat,
    availability_slots: parsedAvailabilitySlots,
    deliveryAvailable: meal.delivery_available || false,
    pickupInstructions: meal.pickup_instructions,
    portionSizeValue: meal.portion_size_value,
    portionSizeUnit: meal.portion_size_unit,
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 pb-20">
        <div className="space-y-8">
          <MealImageGallery
            mainImage={transformedMeal.image}
            thumbnails={[transformedMeal.image]}
            name={transformedMeal.name}
          />
          
          <MealBasicInfo
            name={transformedMeal.name}
            description={transformedMeal.description}
            rating={transformedMeal.rating}
            reviews={transformedMeal.reviews}
            distance={transformedMeal.distance}
            deliveryFee={transformedMeal.deliveryFee}
            mealId={transformedMeal.id}
            price={transformedMeal.price}
            availablePortions={transformedMeal.availablePortions}
            availabilitySlots={parsedAvailabilitySlots}
            chef={transformedMeal.chef}
            pickupInstructions={transformedMeal.pickupInstructions}
            deliveryAvailable={transformedMeal.deliveryAvailable}
            preparationType={transformedMeal.preparationType}
            prepTimeHours={transformedMeal.prepTimeHours}
            cutoffTime={transformedMeal.cutoffTime}
            pickupWindowStart={transformedMeal.pickupWindowStart}
            pickupWindowEnd={transformedMeal.pickupWindowEnd}
            availableDays={transformedMeal.availableDays}
            maxDailyOrders={transformedMeal.maxDailyOrders}
            portionSizeValue={transformedMeal.portionSizeValue}
            portionSizeUnit={transformedMeal.portionSizeUnit}
          />

          {transformedMeal.chef && (
            <MealChefInfo
              chefId={transformedMeal.chef_id}
              name={transformedMeal.chef}
              rating={transformedMeal.chefRating}
              image={transformedMeal.chefImage}
              isCertified={true}
            />
          )}

          {(transformedMeal.calories || transformedMeal.protein || transformedMeal.carbs || transformedMeal.fat) && (
            <NutritionalInfo
              calories={transformedMeal.calories}
              carbs={transformedMeal.carbs}
              protein={transformedMeal.protein}
              fat={transformedMeal.fat}
              portionSizeValue={transformedMeal.portionSizeValue}
              portionSizeUnit={transformedMeal.portionSizeUnit}
            />
          )}

          {(transformedMeal.ingredients?.length > 0 || transformedMeal.allergens?.length > 0 || transformedMeal.dietaryTags?.length > 0) && (
            <MealIngredients
              ingredients={transformedMeal.ingredients}
              allergens={transformedMeal.allergens}
              dietaryTags={transformedMeal.dietaryTags}
            />
          )}

          <MessageChef 
            chefId={transformedMeal.chef_id}
            mealId={transformedMeal.id}
          />

          <MealReviews mealId={transformedMeal.id} />
        </div>
      </div>
    </div>
  );
};

export default MealDetails;
